import { Icon, type IconName } from '../components/icons/Icon'
import { useTheme } from './ThemeProvider'
import type { ThemePreference } from './themePreference'

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; icon: IconName }> = [
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
  { value: 'system', label: 'System', icon: 'monitor' },
]

export interface ThemeToggleProps {
  /**
   * `compact` — icon-only 3-way segmented control for the top app bar
   * (design-system.md §3.1 ThemeToggle: "lives in the user menu"). `full` —
   * icon + visible label, for the Settings/Preferences surface.
   */
  variant?: 'compact' | 'full'
}

/**
 * Light / Dark / System theme preference control (roadmap #11). A tri-state
 * extension of design-system.md §3.1's `ThemeToggle` (`value: light | dark`)
 * — `system` is added so a user can explicitly opt into following the OS,
 * without changing the documented default (a brand-new user still starts on
 * `light`, never silently defaulted to the OS hint; see `themePreference.ts`).
 *
 * Modeled as a `radiogroup` (mutually-exclusive, always exactly one
 * selection) rather than a binary `switch` since there are three states.
 */
export function ThemeToggle({ variant = 'compact' }: ThemeToggleProps) {
  const { preference, setPreference } = useTheme()

  return (
    <div className="iw-theme-toggle" role="radiogroup" aria-label="Theme">
      {OPTIONS.map((opt) => {
        const active = preference === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={opt.label}
            className={
              'iw-theme-toggle__option' + (active ? ' iw-theme-toggle__option--active' : '')
            }
            onClick={() => setPreference(opt.value)}
          >
            <Icon name={opt.icon} size={14} />
            {variant === 'full' ? <span>{opt.label}</span> : <span className="iw-visually-hidden">{opt.label}</span>}
          </button>
        )
      })}
    </div>
  )
}
